import { useEffect, useRef, useState } from 'react';
import { Button } from '../../ui/button';
import { MeetingField } from './MeetingFields';
import { EmailRecipients } from '../EmailRecipients';
import { meetingRequest } from '../../../lib/meetingSummaries';

const split = s => s.split(/[,;\n]+/).map(x => x.trim()).filter(Boolean);
const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/emails/recipients/preview`;

export const MeetingEmailPanel = ({ doc }) => {
  const initial = () => ({ recipients: doc.attendees.map(a => a.email).filter(Boolean).join(', '), cc: '', subject: '', message: '' });
  const [form, setForm] = useState(initial), [review, setReview] = useState(null), [busy, setBusy] = useState(false), [result, setResult] = useState('');
  const lock = useRef(false);
  useEffect(() => { setReview(null); }, [form]);
  const set = k => v => setForm(f => ({ ...f, [k]: v }));
  const send = async () => {
    if (lock.current) return;
    const to = split(form.recipients), cc = split(form.cc);
    if (!to.length) { setResult('Please add at least one recipient.'); return; }
    lock.current = true; setBusy(true); setResult('');
    try {
      if (!review) {
        const res = await fetch(API, { method: 'POST', headers: { Authorization: `Basic ${btoa(':' + sessionStorage.getItem('admin_password'))}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ to, cc }) });
        const data = await res.json();
        if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Please check the recipients.');
        setReview(data); return;
      }
      const data = await meetingRequest(`/${doc.id}/email`, { method: 'POST', body: JSON.stringify({ recipients: review.to_recipients, cc: review.cc, subject: form.subject, message: form.message }) });
      const failed = data.failed.map(f => `${f.recipient}: ${f.error}`).join('; ');
      setResult(failed ? `Sent to ${data.sent.length}. Failed: ${failed}. Check Email History before resending.` : `Sent to ${data.sent.join(', ')} ✓`);
      if (!failed) setForm(initial());
    } catch (e) { setResult(`Failed: ${e.message}`); }
    finally { setBusy(false); lock.current = false; }
  };
  return <section className="mb-5 rounded-xl border-2 border-teal-600 bg-white p-5" data-testid="meeting-email-panel">
    <h2 className="mb-1 text-lg font-semibold text-teal-800">Email summary to attendees</h2>
    <p className="mb-4 text-sm text-gray-600">Recipients are pre-filled from attendee emails. The PDF is attached automatically.</p>
    <div className="grid gap-4 md:grid-cols-2">
      <MeetingField label="To (comma separated)" name="email-to" multiline maxLength={4000} value={form.recipients} onChange={set('recipients')} />
      <MeetingField label="CC (optional)" name="email-cc" multiline maxLength={4000} value={form.cc} onChange={set('cc')} />
      <div className="md:col-span-2"><MeetingField label="Subject (optional)" name="email-subject" value={form.subject} onChange={set('subject')} /></div>
      <div className="md:col-span-2"><MeetingField label="Cover note (optional)" name="email-message" multiline maxLength={10000} value={form.message} onChange={set('message')} /></div>
    </div>
    {review && <div className="mt-4 rounded-lg border border-teal-200 p-3"><EmailRecipients email={review} testId="meeting-email-review" /></div>}
    <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
      {result && <span role="status" data-testid="meeting-email-result" className="break-words text-sm text-gray-700">{result}</span>}
      <Button onClick={send} disabled={busy} data-testid="meeting-email-send">{busy ? (review ? 'Sending…' : 'Reviewing…') : (review ? 'Confirm and send' : 'Review recipients')}</Button>
    </div>
  </section>;
};
